import type { FC } from 'react';
import { useState, useEffect } from 'react';
import {
  Drawer,
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

import type { LcpnTheme } from '../../styles/theme';
import { useFavouritesContext } from '../../providers/FavouritesProvider';
import { useComparisonProviderContext } from '../../providers/ComparisonProvider';
import {
  useUpdateNavigationBar,
  useNavigationBarData,
} from '../NavigationBar/NavigationBarProvider';

import { FavouritesList } from './FavouritesList';

const MAX_COMPARISON_ITEMS = 4;

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  drawerPaper: {
    width: 400,
    maxWidth: '90vw',
  },
  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    padding: theme.spacing(4),
    borderBottom: `1px solid ${theme.colors['grey-100']}`,
    backgroundColor: theme.colors['blue-100'],
    color: 'white',
  },
  tabsContainer: {
    borderBottom: `1px solid ${theme.colors['grey-100']}`,
  },
  listContainer: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(4),
  },
  selectAllControl: {
    marginLeft: 0,
    marginBottom: theme.spacing(2),
    paddingLeft: 0,
    gap: 0,
    '& .MuiCheckbox-root': {
      padding: theme.spacing(1),
      marginRight: 0,
    },
    '& .MuiTypography-root': {
      paddingLeft: theme.spacing(4),
    },
  },
  footer: {
    padding: theme.spacing(4),
    borderTop: `1px solid ${theme.colors['grey-100']}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  emptyState: {
    padding: theme.spacing(8),
    textAlign: 'center',
    color: theme.colors.darker,
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
}

export const FavouritesDrawer: FC<Props> = ({ open, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'poi' | 'heatmap'>('poi');
  const [selectedPoi, setSelectedPoi] = useState<string[]>([]);
  const [selectedHeatmap, setSelectedHeatmap] = useState<string[]>([]);

  const {
    poiFavourites,
    heatmapFavourites,
    togglePoiFavourite,
    toggleHeatmapFavourite,
  } = useFavouritesContext();
  const { setPoiComparison, setHeatMapComparison } =
    useComparisonProviderContext();
  const { updateSelectedNavigationItem } = useUpdateNavigationBar();
  const { selectedExperience } = useNavigationBarData();

  // Auto-switch tab when drawer opens based on current experience
  useEffect(() => {
    if (open) {
      if (selectedExperience === 'pinMap') {
        setActiveTab('poi');
      } else if (selectedExperience === 'heatMap') {
        setActiveTab('heatmap');
      }
    }
  }, [open, selectedExperience]);

  const activeFavourites =
    activeTab === 'poi' ? poiFavourites : heatmapFavourites;
  const selectedItems = activeTab === 'poi' ? selectedPoi : selectedHeatmap;
  const setSelectedItems =
    activeTab === 'poi' ? setSelectedPoi : setSelectedHeatmap;

  const handleCompare = () => {
    // Blur the active element to prevent focus being trapped in closing drawer
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (activeTab === 'poi' && selectedPoi.length >= 2) {
      // Set context with syncVersion to force picker re-initialization
      // Note: Picker will automatically save to localStorage when it updates
      setPoiComparison({
        display: false,
        studyIds: selectedPoi,
        syncVersion: Date.now(),
      });

      updateSelectedNavigationItem('comparison');
      onClose();
    } else if (activeTab === 'heatmap' && selectedHeatmap.length >= 2) {
      // Set context with syncVersion to force picker re-initialization
      // Note: Picker will automatically save to localStorage when it updates
      setHeatMapComparison({
        display: false,
        countryCodes: selectedHeatmap,
        syncVersion: Date.now(),
      });

      updateSelectedNavigationItem('comparison');
      onClose();
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      }
      if (prev.length >= MAX_COMPARISON_ITEMS) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const deleteSelectedFavourites = () => {
    // Delete selected POI favourites
    for (const id of selectedPoi) {
      togglePoiFavourite(id);
    }

    // Delete selected heatmap favourites
    for (const code of selectedHeatmap) {
      toggleHeatmapFavourite(code);
    }

    // Clear selection arrays
    setSelectedPoi([]);
    setSelectedHeatmap([]);
  };

  const clearSelectionState = () => {
    // Only clear the selection state, don't delete favourites
    setSelectedPoi([]);
    setSelectedHeatmap([]);
  };

  const handleSelectAll = () => {
    const itemsToSelect = activeFavourites.slice(0, MAX_COMPARISON_ITEMS);
    const allSelected =
      selectedItems.length === itemsToSelect.length &&
      itemsToSelect.every(item => selectedItems.includes(item));

    if (allSelected) {
      // Unselect all
      setSelectedItems([]);
    } else {
      // Select first MAX_COMPARISON_ITEMS
      setSelectedItems(itemsToSelect);
    }
  };

  const getSelectAllState = () => {
    const itemsToSelect = activeFavourites.slice(0, MAX_COMPARISON_ITEMS);
    const allSelected =
      selectedItems.length === itemsToSelect.length &&
      itemsToSelect.every(item => selectedItems.includes(item));
    const someSelected = selectedItems.length > 0 && !allSelected;

    return { checked: allSelected, indeterminate: someSelected };
  };

  const handleClose = () => {
    clearSelectionState();
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.drawerContent}>
        <div className={classes.header}>
          <Typography variant="h6">{t('favourites.myFavourites')}</Typography>
          <Typography variant="caption">
            {poiFavourites.length + heatmapFavourites.length}{' '}
            {t('favourites.total')}
          </Typography>
        </div>

        <div className={classes.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab
              label={`${t('favourites.implementationTab')} (${
                poiFavourites.length
              })`}
              value="poi"
            />
            <Tab
              label={`${t('favourites.policyTab')} (${
                heatmapFavourites.length
              })`}
              value="heatmap"
            />
          </Tabs>
        </div>

        <div className={classes.listContainer}>
          {activeFavourites.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body1">
                {t('favourites.noFavouritesYet')}
              </Typography>
              <Typography variant="body2">
                {t('favourites.noFavouritesDescription')}
              </Typography>
            </div>
          ) : (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    edge="start"
                    checked={getSelectAllState().checked}
                    indeterminate={getSelectAllState().indeterminate}
                    onChange={handleSelectAll}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('favourites.selectAll', {
                      maxCount: MAX_COMPARISON_ITEMS,
                    })}
                  </Typography>
                }
                className={classes.selectAllControl}
              />
              <FavouritesList
                type={activeTab}
                favourites={activeFavourites}
                selectedItems={selectedItems}
                onToggleSelection={toggleSelection}
              />
            </>
          )}
        </div>

        {activeFavourites.length > 0 && (
          <div className={classes.footer}>
            <Typography variant="caption" sx={{ mb: 1 }}>
              {t('favourites.selectItemsToCompare', {
                maxCount: MAX_COMPARISON_ITEMS,
                count: selectedItems.length,
              })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={deleteSelectedFavourites}
                disabled={selectedItems.length === 0}
              >
                {t('favourites.clearSelection')}
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleCompare}
                disabled={
                  selectedItems.length < 2 ||
                  selectedItems.length > MAX_COMPARISON_ITEMS
                }
              >
                {selectedItems.length >= 2
                  ? t('favourites.compareWithCount', {
                      count: selectedItems.length,
                    })
                  : t('favourites.compare')}
              </Button>
            </Box>
          </div>
        )}
      </div>
    </Drawer>
  );
};
